<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import KnowledgeBaseForm from './KnowledgeBaseForm.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  selectedKnowledgeBase: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close', 'createSuccess', 'updateSuccess']);
const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);

const i18nKey = computed(
  () => `ASSISTANT.KNOWLEDGE_BASES.${props.type.toUpperCase()}`
);

const handleSubmit = async knowledgeBaseDetails => {
  try {
    if (props.type === 'edit') {
      await store.dispatch('assistantKnowledgeBases/update', {
        id: props.selectedKnowledgeBase.id,
        ...knowledgeBaseDetails,
      });
      emit('updateSuccess');
    } else {
      await store.dispatch(
        'assistantKnowledgeBases/create',
        knowledgeBaseDetails
      );
      emit('createSuccess');
    }
    useAlert(t(`${i18nKey.value}.SUCCESS_MESSAGE`));
    dialogRef.value.close();
  } catch (error) {
    const errorMessage =
      error?.message ||
      parseAPIErrorResponse(error) ||
      t(`${i18nKey.value}.ERROR_MESSAGE`);
    useAlert(errorMessage);
  }
};

const handleClose = () => {
  emit('close');
};

const handleCancel = () => {
  dialogRef.value.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t(`${i18nKey}.TITLE`)"
    :description="$t('ASSISTANT.KNOWLEDGE_BASES.FORM_DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleClose"
  >
    <KnowledgeBaseForm
      :mode="type"
      :knowledge-base="selectedKnowledgeBase"
      @submit="handleSubmit"
      @cancel="handleCancel"
    />
    <template #footer />
  </Dialog>
</template>
